/**
 * How the app gets a bearer token.
 *
 * The Cognito hosted UI does not exist yet (sc-178487), and the pages must not
 * have to change when it arrives — so every caller depends on this interface
 * rather than on an SDK.
 */
export interface TokenProvider {
  /**
   * A Cognito **access** token. Browser and MCP callers share one credential
   * type: an MCP client only ever receives an access token (sc-178757), so
   * unifying on it keeps a single code path through the API. The `email` claim
   * the API reads for `uploadedBy` is added by a pre-token-generation trigger
   * (sc-178487).
   */
  getAccessToken(): Promise<string>;
  signOut?(): Promise<void>;
}

/**
 * Development stand-in: reads a token pasted into `VITE_DEV_ACCESS_TOKEN`.
 *
 * It exists so the SPA can be run against a deployed API before the hosted UI
 * lands. It refuses to produce a token in a production build, so a missing
 * Cognito wiring fails loudly instead of silently falling back to whatever a
 * bundled environment variable happens to hold.
 */
export function createDevTokenProvider(
  env: { VITE_DEV_ACCESS_TOKEN?: string; PROD?: boolean } = import.meta.env,
): TokenProvider {
  return {
    async getAccessToken() {
      if (env.PROD) {
        throw new Error(
          "the development token provider must not be used in a production build",
        );
      }
      const token = env.VITE_DEV_ACCESS_TOKEN?.trim();
      if (!token) {
        throw new Error(
          "VITE_DEV_ACCESS_TOKEN is not set; paste a Cognito access token to run against the API",
        );
      }
      return token;
    },
  };
}
